/**
 * Default Input Values for Solar + BESS Financial Model
 * Optimized for Philippines Visayas Region Market Conditions
 */

export const DEFAULT_PROJECT_INPUTS = {
    // Project Fundamentals - Optimized for Philippines Visayas Region
    constructionPeriod: 1,
    operationPeriod: 25,
    installedCapacity: 50,
    investmentPerMW: 500000, // $0.5M/MW - competitive for Philippines market
    annualHours: 1950, // ~5.35 kWh/m²/day excellent Visayas solar resource
    onGridTariff: 0.198, // $0.198/kWh (~P11.78/kWh current Visayan Electric rates)
    pvDegradationRate: 0.005, // 0.5% per year (tropical climate consideration)
    operatingCostRate: 0.08, // 8% of revenue (optimized for Philippines)
    operatingCostEscalationRate: 0.025, // 2.5% inflation (Philippines target rate)
    ownFunds: 12500000, // $12.5M equity for 50 MW project (20% equity ratio)
    longTermLoanRate: 0.075, // 7.5% (competitive Philippines renewable financing)
    depreciationPeriod: 20, // 20 years (optimized tax depreciation)
    salvageValueRate: 0.08, // 8% salvage value (higher for quality equipment)
    vatRate: 0.12, // 12% (Philippines VAT rate)
    surtaxRate: 0.10, // 10% (optimized surtax)
    incomeTaxRate: 0.25, // 25% (Philippines corporate tax rate)
    statutoryReserveRate: 0.10,
    discretionaryReserveRate: 0.05,
    profitDistributionRate: 0.75,
    discountRate: 0.08, // 8% (appropriate for Philippines renewable projects)
    goalSeekTargetIRR: 0.12, // 12% target IRR (competitive for market)
    landCostPerAcre: 2500, // $2,500/acre (Philippines agricultural land lease)
    acresPerMW: 4.5, // 4.5 acres/MW (optimized land use efficiency)

    // BESS Parameters - Enabled by default for competitive edge
    bessEnabled: false,
    bessEnergyCapacity: 100, // 100 MWh (2-hour storage for 50 MW)
    bessPowerCapacity: 50, // 50 MW (1:1 ratio with solar)
    bessCostPerMWh: 100000, // $100k/MWh (competitive current market pricing)
    bessPowerCostPerMW: 100000, // $100k/MW (optimized power electronics cost)
    bessRoundTripEfficiency: 0.87, // 87% (latest LFP technology efficiency)
    bessDegradationRate: 0.018, // 1.8%/year (optimized battery degradation)
    bessOperatingCostRate: 10000, // $10k/MW/year (reduced O&M costs)
    bessReplacementPeriod: 15, // 15 years replacement cycle
    bessReplacementCostFactor: 0.35, // 35% of original cost (technology learning curve)

    // BESS Revenue Streams - Optimized for Philippines Grid Services
    bessArbitrageCycles: 1.2, // 1.2 cycles/day (peak shaving + night dispatch)
    bessArbitrageSpread: 0.35, // 35% price spread (Philippines grid volatility)
    bessAncillaryRate: 28000, // $28k/MW/year (frequency regulation services)
    bessCapacityRate: 6000, // $6k/MW/month (capacity payments)
    bessDemandChargeReduction: 9500, // $9.5k/MW/month (peak demand reduction)
};

/**
 * Alternative input presets for different regions/scenarios
 */
export const INPUT_PRESETS = {
    PHILIPPINES_VISAYAS: DEFAULT_PROJECT_INPUTS,
    
    PHILIPPINES_LUZON: {
        ...DEFAULT_PROJECT_INPUTS,
        annualHours: 1850, // Slightly lower solar resource
        onGridTariff: 0.185, // Different grid rates
        landCostPerAcre: 3500, // Higher land costs
    },
    
    PHILIPPINES_MINDANAO: {
        ...DEFAULT_PROJECT_INPUTS,
        annualHours: 1900, // Good solar resource
        onGridTariff: 0.175, // Lower grid rates
        landCostPerAcre: 2000, // Lower land costs
    },
    
    CONSERVATIVE_SCENARIO: {
        ...DEFAULT_PROJECT_INPUTS,
        annualHours: 1750, // Lower solar resource
        operatingCostRate: 0.10, // Higher operating costs
        pvDegradationRate: 0.007, // Higher degradation
        goalSeekTargetIRR: 0.10, // Lower target IRR
    },
    
    AGGRESSIVE_SCENARIO: {
        ...DEFAULT_PROJECT_INPUTS,
        annualHours: 2100, // Higher solar resource
        operatingCostRate: 0.06, // Lower operating costs
        pvDegradationRate: 0.004, // Lower degradation
        goalSeekTargetIRR: 0.15, // Higher target IRR
    }
};

/**
 * Input categories for organization
 */
export const INPUT_CATEGORIES = {
    PROJECT_FUNDAMENTALS: [
        'constructionPeriod',
        'operationPeriod', 
        'installedCapacity',
        'investmentPerMW',
        'annualHours',
        'onGridTariff',
        'pvDegradationRate'
    ],
    
    COSTS_AND_FINANCING: [
        'operatingCostRate',
        'operatingCostEscalationRate',
        'ownFunds',
        'longTermLoanRate',
        'landCostPerAcre',
        'acresPerMW'
    ],
    
    TAX_AND_DEPRECIATION: [
        'depreciationPeriod',
        'salvageValueRate',
        'vatRate',
        'surtaxRate',
        'incomeTaxRate'
    ],
    
    PROFIT_DISTRIBUTION: [
        'statutoryReserveRate',
        'discretionaryReserveRate',
        'profitDistributionRate',
        'discountRate',
        'goalSeekTargetIRR'
    ],
    
    BESS_CONFIGURATION: [
        'bessEnabled',
        'bessEnergyCapacity',
        'bessPowerCapacity',
        'bessCostPerMWh',
        'bessPowerCostPerMW',
        'bessRoundTripEfficiency',
        'bessDegradationRate',
        'bessOperatingCostRate',
        'bessReplacementPeriod',
        'bessReplacementCostFactor'
    ],
    
    BESS_REVENUE_STREAMS: [
        'bessArbitrageCycles',
        'bessArbitrageSpread',
        'bessAncillaryRate',
        'bessCapacityRate',
        'bessDemandChargeReduction'
    ]
}; 